import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { capitalize, getBaseRoute } from "@/lib/utils";
import { setCommunityData, setPropertyData } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import {
  useBulkDownloadProperty,
  useDeletePropertyMutation,
  useDownloadPropertyTemplate,
  useGetBomUsersList,
  useGetPropertiesByCommunity,
  useGetPropertyDeleteState,
  useGetCommunities,
} from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { MoreVertical, Settings, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "@/components/Toast";

const useProperties = () => {
  const {id: commId} = useParams();

  const [globalFilter, setGlobalFilter] = useState("");
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [openOwnershipDialog, setOpenOwnershipDialog] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [showBulkDialog, setShowBulkDialog] = useState<boolean>(false);
  const [file, setFile] = useState<any>(null);
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const fileInputRef = useRef<any>(null);
  const location = useLocation();
  const isMasterData = location?.pathname?.toLowerCase()?.includes("master-data");
  const basePath = getBaseRoute(useLocation().pathname);
  const isAdminBasePath = basePath == "admin";

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const masterDataPermission = useSelector(
    (state: RootState) => state.currentData.masterDataPermission
  );

  const id = isMasterData ? communityDetail?.communityId : commId;

  const isCamBom = location.pathname.startsWith("/cam");

  const hasAddPermission = useMemo(() => (isCamBom ? masterDataPermission?.add : true), [masterDataPermission, isCamBom]);
  // Edit moved to the property detail page (Edit Property there)
  // const hasUpdatePermission = useMemo(() => (isCamBom ? masterDataPermission?.update : true), [masterDataPermission, isCamBom]);
  const hasDeletePermission = useMemo(() => (isCamBom ? masterDataPermission?.delete : true), [masterDataPermission, isCamBom]);

  const navigatePath = isAdminBasePath ? 
  `/${basePath}/${routes.adminConsole}/${routes.communities}/${id}/${routes.properties}` : `/${basePath}/${routes.communities}/${id}/${routes.properties}`

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const { data: communityList, isLoading: isCommunityLoading } =
    useGetCommunities({});

  useEffect(() => {
    if (communityList) {
      if (communityDetail) {
      } else {
        dispatch(setCommunityData(communityList?.data?.[0]));
      }
    }
  }, [communityList]);

  const onCommunityChange = (e: any) => {
    const details = communityList?.data?.find(
      (data: any) => data.communityId == e
    );
    dispatch(setCommunityData(details));
  };

  //get properties
  const { data: propertiesData, isLoading: isLoadingProperties } =
    useGetPropertiesByCommunity({
      communityId: id as string,
    });

  const { data: bomUserList, isLoading: isLoadingBom } = useGetBomUsersList({
    communityId: id as string,
  });

  const { data: propertyDeleteState, isLoading: isLoadingPropertyDeleteState } =
    useGetPropertyDeleteState({
      communityId: communityDetail?.communityId,
      id: selectedRow?.propertyId,
      queryParams: { enabled: !!selectedRow?.propertyId },
    });

  const { mutate: deleteInfo, isPending: isDeletePending } =
    useDeletePropertyMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["properties", id],
        });
        navigate(navigatePath);
        toast.success("Property Deleted Successfully");
      },
    });

  const communityBreadcrumbData = [
    {
      link: `/${basePath}/${routes.adminConsole}/${routes.communities}`,
      name: `Community - ${communityDetail?.name}`,
    },
    {
      link: "",
      name: <span className="text-primary">{"Manage Property"}</span>,
    },
  ];

  const breadcrumbData = isMasterData ? null : communityBreadcrumbData;
  const title = isMasterData ? "Manage Properties" : communityDetail?.name;

  const {
    data: downloadTemplateData,
    refetch: refetchTemplateData,
    isRefetching: isTemplateDownloading,
  } = useDownloadPropertyTemplate({ 
    communityId: communityDetail?.communityId,
    queryParams: {
      enabled: false 
    }
  });

  const {
    data: bulkDownload,
    refetch: refetchBulkData,
    isRefetching: isBulkDownloading,
  } = useBulkDownloadProperty({
    communityId: id as string,
    queryParams: {
      enabled: false,
    },
  });

  useEffect(() => {
    if (downloadTemplateData) {
      try {
        const response = downloadTemplateData;
        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const contentDisposition = response.headers["content-disposition"];
        let downloadFileName = `property-template.xlsx`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=\"?([^\"]+)\"?/);
          if (match?.[1]) {
            downloadFileName = match[1];
          }
        }

        link.setAttribute("download", downloadFileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ||
            "There is an issue while downloading the file"
        );
      } finally {
        queryClient.removeQueries({ queryKey: ["download-property-template", communityDetail?.communityId] });
      }
    }
  }, [downloadTemplateData, id]);

  useEffect(() => {
    if (bulkDownload) {
      try {
        const response = bulkDownload;
        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const contentDisposition = response.headers["content-disposition"];
        let downloadFileName = `${id}-properties.xlsx`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=\"?([^\"]+)\"?/);
          if (match?.[1]) {
            downloadFileName = match[1];
          }
        }

        link.setAttribute("download", downloadFileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ||
            "There is an issue while downloading the file"
        );
      } finally {
        queryClient.removeQueries({ queryKey: ["bulk-download-property", id] });
      }
    }
  }, [bulkDownload, id]);

  // Define Table Columns
  const columns = [
    {
      accessorKey: "displayId",
      header: "Property ID",
      accessorFn: (row: any) => String(row.displayId),
    },
    {
      accessorKey: "name",
      header: "Property Name",
      accessorFn: (row: any) => String(row?.name || ""),
    },
    {
      accessorKey: "unitNumber",
      header: "Unit Number",
      accessorFn: (row: any) => String(row?.unitNumber || ""),
    },
    {
      accessorKey: "ownershipId",
      header: "Ownership ID",
      accessorFn: (row: any) => String(row?.ownershipId || ""),
    },
    {
      accessorKey: "occupancyType",
      header: "Residency Type",
      accessorFn: (row: any) => {
        return row?.occupancyType ? capitalize(row?.occupancyType) : "";
      },
    },
    {
      accessorKey: "primaryUserName",
      header: "Current Occupant",
      accessorFn: (row: any) => String(row?.primaryUserName || ""),
    },
    {
      accessorKey: "bomPosition",
      header: "BM Position",
      accessorFn: (row: any) =>
        bomUserList?.data?.find(
          (data: any) => row?.primaryUserId == data?.userId
        )?.positionName,
    },
    {
      accessorKey: "userCount",
      header: "# of Users",
      cell: ({ row }: any) => {
        return row?.original?.userCount ?
          isMasterData ?
            <div className="px-3">
              {String(row?.original?.userCount)}
            </div> :
            <div
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                dispatch(setPropertyData(row.original));
                navigate(`${row.original.propertyId}/${routes.users}`);
              }}
              className="text-blue-500 hover:text-blue-600 cursor-pointer px-3"
            >
              {String(row?.original?.userCount)}
            </div>
        : (
          <div className="px-3">{"0"}</div>
        );
      },
    },
    ...(hasDeletePermission ? 
      [{
        id: "actions",
        header: "Actions",
        cell: (row: any) => (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <MoreVertical className="w-5 h-5 cursor-pointer" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white border-0 shadow-md"
            >
              {hasDeletePermission ? <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setSelectedRow(row.row.original);
                  setOpenDialog(true);
                }}
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </DropdownMenuItem> : <></>}
              {/* {isMasterData && row?.row?.original?.userCount ?
                <DropdownMenuItem
                  className="flex items-center gap-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setSelectedRow(row.row.original);
                    setOpenOwnershipDialog(true);
                  }}
                >
                  <Eye className="w-4 h-4" />
                  View Ownership History
                </DropdownMenuItem> : <></>} */}
                {isMasterData ? <></> : <DropdownMenuItem
                  className="flex items-center gap-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    dispatch(setPropertyData(row.row.original));
                    navigate(
                        row.row.original.propertyId +
                        "/" +
                        routes.users
                    );
                  }}
                >
                  <Settings className="w-4 h-4" />
                  Manage Users
                </DropdownMenuItem>
              }
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      }] : 
    []),
  ];

  const onAddProperties = () => {
    navigate(`${routes.add}`);
  };

  const onClose = () => {
    setOpenDialog(false);
  };

  const onConfirm = (reason: string) => {
    deleteInfo({ id: selectedRow?.propertyId, reason, communityId: communityDetail?.communityId });
    onClose();
  };

  const onRowClick = (row: any) => {
    dispatch(setPropertyData(row.original));
    navigate(`${row.original.propertyId}/view`);
  };

  const downloadTemplate = () => {
    // if (selectedType === "owner") {
    //   refetchUserTemplateData();
    //   return;
    // }
    refetchTemplateData();
  };

  const onBulkDownload = () => {
    // if (selectedType === "owner") {
    //   refetchBulkUserData();
    //   return;
    // }
    refetchBulkData();
  };

  const onUpload = () => {
    if (!file) {
      toast.success("Please select file");
      return;
    }
  };
  
  const onBulkUpload = () => {
    navigate(`${routes.bulkUpload}`)
  }

  return {
    onAddProperties,
    globalFilter,
    setGlobalFilter,
    columns,
    breadcrumbData,
    isLoadingProperties,
    propertiesData,
    communityDetail,
    openDialog,
    onClose,
    onConfirm,
    isDeletePending,
    propertyDeleteState,
    isLoadingPropertyDeleteState,
    selectedRow,
    onRowClick,
    isLoadingBom,
    isTemplateDownloading,
    downloadTemplate,
    setShowBulkDialog,
    showBulkDialog,
    file,
    fileInputRef,
    setFile,
    onUpload,
    isBulkDownloading,
    onBulkDownload,
    communityList,
    communityId: id,
    isCommunityLoading,
    onCommunityChange,
    isMasterData,
    title,
    openOwnershipDialog,
    setOpenOwnershipDialog,
    onBulkUpload,
    hasAddPermission
  };
};

export default useProperties;
